import { memo, useState } from 'react';

import { Form } from 'src/shared/components/form/form.js';
import { Input } from 'src/shared/components/input/input.js';
import { MessageToast } from 'src/shared/components/message-toast/message-toast.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { useRedirectToSignUp } from 'src/shared/hooks/use-redirect-to-sign-up-on-email-error.js';
import { useSavedFieldLs } from 'src/shared/hooks/use-saved-field-ls.js';
import type { KcContext_LoginUsername } from 'src/shared/utils/kc-context/kc-context.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

export interface LoginUsernameProps {
    kcContext: KcContext_LoginUsername;
}

export const LoginUsername = memo<LoginUsernameProps>(({ kcContext }) => {
    const { url, login, message } = kcContext;
    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);
    const [onChangeSavedField, getSavedField] = useSavedFieldLs();

    const [showSuccessMessage, setShowSuccessMessage] = useState(() =>
        Boolean(message?.type === 'success' && message?.summary),
    );

    const emailError = useFieldMessage(['email', 'username'], kcContext);

    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(['username'], {
        username: emailError,
    });

    useRedirectToSignUp(kcContext);
    if (emailError) return null;

    return (
        <Form action={url.loginAction} kcContext={kcContext} onSubmit={onSubmitValidated} title={msgStr('mainTitle')}>
            {showSuccessMessage && (
                <MessageToast message={message?.summary || ''} onClose={() => setShowSuccessMessage(false)} />
            )}
            <Input
                type="text"
                id="username"
                name="username"
                defaultValue={login.username ?? getSavedField('email')}
                label={msgStr('email')}
                autoComplete="email"
                autoFocus={!emailError}
                errorMessage={advancedMsgStr(formErrors['email'] ?? '')}
                onFocus={resetFieldError}
                onChange={onChangeSavedField}
            />
            <Input hidden type="checkbox" id="rememberMe" name="rememberMe" checked />
            <Input type="submit" value={msgStr('doLogIn')} disabled={submitDisabled} />
        </Form>
    );
});
